<template>
    <Card pageTitle="Cost Adjustment Details">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_ADJUSTMENT">
            <BackNavigateBtn title="Back to Cost Adjustments" to="/inventory-adjustments/cost" />

            <div v-if="loading" class="loading-state">
                <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div>
                <p>Loading cost adjustment...</p>
            </div>

            <template v-else>
                <header class="detail-header">
                    <div>
                        <span class="eyebrow">Completed cost adjustment</span>
                        <h3>{{ adjustment.name }}</h3>
                        <p>{{ formatDateTime(adjustment.createdAt) }}</p>
                    </div>
                    <dl class="detail-summary">
                        <div><dt>Source</dt><dd>{{ adjustment.source_name || "Purchase order" }}</dd></div>
                        <div><dt>Supplier</dt><dd>{{ adjustment.supplier_name || "—" }}</dd></div>
                        <div><dt>Location</dt><dd>{{ adjustment.warehouse_name || "—" }}</dd></div>
                        <div><dt>Completed by</dt><dd>{{ userName(adjustment.user) }}</dd></div>
                    </dl>
                </header>

                <div class="table-responsive detail-table-wrap">
                    <table class="detail-table">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Supplier SKU</th>
                                <th>Batch / Package</th>
                                <th>Location</th>
                                <th class="numeric">Previous Cost</th>
                                <th class="numeric">New Cost</th>
                                <th class="numeric">Change</th>
                                <th class="numeric">Received Qty</th>
                                <th>Completed By</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in items" :key="item.id">
                                <td><strong>{{ item.item_name || item.item_code }}</strong><small>{{ item.item_code }}</small></td>
                                <td>{{ item.supplier_sku || "—" }}</td>
                                <td>{{ item.batch_no || "—" }}</td>
                                <td>{{ item.warehouse ? item.warehouse.name : adjustment.warehouse_name || "—" }}</td>
                                <td class="numeric">{{ money(item.previous_cost) }}</td>
                                <td class="numeric">{{ money(item.new_cost) }}</td>
                                <td class="numeric">
                                    <span :class="changeClass(item.cost_change)">{{ signedMoney(item.cost_change) }}</span>
                                    <small>{{ signedPercent(item.cost_change_percentage) }}</small>
                                </td>
                                <td class="numeric">{{ number(item.received_qty) }}</td>
                                <td>{{ userName(item.user) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </AllowPermission>
    </Card>
</template>

<script>
import AllowPermission from "../../../components/layout/AllowPermission.vue"
import BackNavigateBtn from "../../../components/UI/BackNavigateBtn.vue"
import Card from "../../../components/UI/Card.vue"
import { PERMISSION_CODES } from "../../../store/permission"
import CostFetchApi from "../../../utils/fetch/cost-fetch-api"

export default {
    components: { AllowPermission, BackNavigateBtn, Card },
    data() {
        return { loading: true, adjustment: {}, items: [], PERMISSION_CODES }
    },
    async mounted() {
        try {
            const data = await CostFetchApi.getCostAdjustmentDetails(this.$route.params.id)
            this.adjustment = data.result || {}
            this.items = data.items || []
        } catch (err) {
            console.error("Error fetching cost adjustment details", err)
            this.$toast.error("Failed to load cost adjustment details")
        } finally {
            this.loading = false
        }
    },
    methods: {
        money(value) {
            return Number(value || 0).toLocaleString("en-CA", { style: "currency", currency: "CAD" })
        },
        signedMoney(value) {
            const number = Number(value || 0)
            return `${number > 0 ? "+" : ""}${this.money(number)}`
        },
        number(value) {
            return Number(value || 0).toLocaleString("en-CA", { maximumFractionDigits: 4 })
        },
        signedPercent(value) {
            if (value === null || value === undefined || value === "") return "—"
            const number = Number(value)
            return `${number > 0 ? "+" : ""}${number.toFixed(2)}%`
        },
        changeClass(value) {
            const number = Number(value || 0)
            return number > 0 ? "increase" : number < 0 ? "decrease" : ""
        },
        userName(user) {
            if (!user) return "—"
            return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.username || "—"
        },
        formatDateTime(value) {
            if (!value) return "—"
            return new Date(value).toLocaleString("en-CA", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
            })
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.loading-state { padding: 56px 20px; text-align: center; color: rgba($textColor, 0.65); }
.loading-state p { margin: 10px 0 0; }

.detail-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: $sectionGap * 2;
    margin: $sectionGap 0 $sectionGap * 1.5;
    padding-bottom: $sectionGap * 1.5;
    border-bottom: 1px solid $lightBorderColor;
}

.eyebrow { display: block; margin-bottom: 6px; color: $primaryColor; font-size: 0.76rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.detail-header h3 { margin: 0 0 5px; }
.detail-header p { margin: 0; color: rgba($textColor, 0.62); }

.detail-summary { display: grid; grid-template-columns: repeat(2, minmax(150px, 1fr)); gap: 14px 28px; margin: 0; }
.detail-summary div { min-width: 0; }
.detail-summary dt { color: rgba($textColor, 0.55); font-size: 0.75rem; font-weight: 600; text-transform: uppercase; }
.detail-summary dd { margin: 3px 0 0; color: $textColor; font-weight: 600; }

.detail-table-wrap { border: 1px solid $lightBorderColor; border-radius: $cardRadius; }
.detail-table { width: 100%; min-width: 1080px; border-collapse: collapse; }
.detail-table th { padding: 12px 14px; border-bottom: 1px solid $lightBorderColor; background: rgba($primaryColor, 0.035); color: rgba($textColor, 0.68); font-size: 0.76rem; text-align: left; text-transform: uppercase; }
.detail-table td { padding: 14px; border-bottom: 1px solid rgba($lightBorderColor, 0.75); vertical-align: middle; }
.detail-table tbody tr:last-child td { border-bottom: 0; }
.detail-table small { display: block; margin-top: 3px; color: rgba($textColor, 0.54); }
.numeric { text-align: right !important; }
.increase { color: #267a4b; font-weight: 700; }
.decrease { color: #a2483e; font-weight: 700; }

@media (max-width: 900px) {
    .detail-header { flex-direction: column; }
    .detail-summary { width: 100%; }
}

@media (max-width: 560px) {
    .detail-summary { grid-template-columns: 1fr; }
}
</style>
